import { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import { useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";
import {
  getSaleCompanyMatrixSetting,
  postSaleCompanyMatrixSetting,
  MatrixSettingData,
} from "../api/saleCompanyMatrixSetting";

interface SaleCompanyBulkSettingModalProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  settingType: "entry" | "tossup" | "closing_partner" | "closing_category" | "collaborate";
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: "1200px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  overflow: "auto",
};

const headerCellStyle = {
  fontSize: "0.7rem",
  padding: "6px 4px",
  whiteSpace: "nowrap" as const,
};

const stickyHeaderCellStyle = {
  fontSize: "0.7rem",
  padding: "6px 4px",
  whiteSpace: "nowrap" as const,
  position: "sticky" as const,
  left: 0,
  zIndex: 3,
  backgroundColor: "rgb(230, 230, 240)",
  borderRight: "2px solid #e0e0e0",
};

const colHeaderCellStyle = {
  fontSize: "0.7rem",
  px: 1.5,
  whiteSpace: "nowrap" as const,
  borderRight: "1px solid #e0e0e0",
  backgroundColor: "rgb(230, 230, 240)",
};

const bodyCheckboxCellStyle = {
  padding: "4px 2px",
  borderRight: "1px solid #e0e0e0",
  textAlign: "center" as const,
};

export function SaleCompanyBulkSettingModal({
  open,
  onClose,
  onSaveSuccess,
  settingType,
}: SaleCompanyBulkSettingModalProps) {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const [matrixData, setMatrixData] = useState<MatrixSettingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'' | 'success' | 'error'>('');

  const [entrySet, setEntrySet] = useState<Set<string>>(new Set());
  const [tossUpSet, setTossUpSet] = useState<Set<string>>(new Set());
  const [closingPartnerSet, setClosingPartnerSet] = useState<Set<string>>(new Set());
  const [closingCategorySet, setClosingCategorySet] = useState<Set<string>>(new Set());
  const [collaborateSet, setCollaborateSet] = useState<Set<string>>(new Set());

  const [initialEntrySet, setInitialEntrySet] = useState<Set<string>>(new Set());
  const [initialTossUpSet, setInitialTossUpSet] = useState<Set<string>>(new Set());
  const [initialClosingPartnerSet, setInitialClosingPartnerSet] = useState<Set<string>>(new Set());
  const [initialClosingCategorySet, setInitialClosingCategorySet] = useState<Set<string>>(new Set());
  const [initialCollaborateSet, setInitialCollaborateSet] = useState<Set<string>>(new Set());

  const SETTING_TYPE_LABEL: Record<string, string> = {
    entry: "エントリー商材",
    tossup: "トスアップ商材",
    closing_partner: "トスアップ商材 - 指定クロージングパートナー",
    closing_category: "グループ - クロージングカテゴリー",
    collaborate: "協業パートナー",
  };

  useEffect(() => {
    if (!open) {
      setMatrixData(null);
      setSaveResult('');
      return;
    }
    setLoading(true);
    setIsLoading(true);
    getSaleCompanyMatrixSetting().then((data) => {
      if (data) {
        setMatrixData(data);

        const es = new Set<string>(
          data.entry_settings.map((e) => `${e.sale_company_id}_${e.product_id}`)
        );
        setEntrySet(es);
        setInitialEntrySet(new Set(es));

        const ts = new Set<string>(
          data.toss_up_settings.map((t) => `${t.sale_company_id}_${t.product_id}`)
        );
        setTossUpSet(ts);
        setInitialTossUpSet(new Set(ts));

        const cps = new Set<string>(
          data.closing_partner_settings.map(
            (cp) => `${cp.sale_company_toss_up_product_id}_${cp.closer_company_id}`
          )
        );
        setClosingPartnerSet(cps);
        setInitialClosingPartnerSet(new Set(cps));

        const ccs = new Set<string>(
          data.closing_category_settings.map(
            (cc) => `${cc.sale_company_group_id}_${cc.category_id}`
          )
        );
        setClosingCategorySet(ccs);
        setInitialClosingCategorySet(new Set(ccs));

        const colls = new Set<string>(
          data.collaborate_settings.map((c) => {
            const a = c.sale_company_id;
            const b = c.collaborate_company_id;
            return `${Math.min(a, b)}_${Math.max(a, b)}`;
          })
        );
        setCollaborateSet(colls);
        setInitialCollaborateSet(new Set(colls));
      }
      setLoading(false);
      setIsLoading(false);
    });
  }, [open]);

  function toggleSet(
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    key: string
  ) {
    setter((prev) => {
      const s = new Set(prev);
      if (s.has(key)) {
        s.delete(key);
      } else {
        s.add(key);
      }
      return s;
    });
  }

  async function handleSave() {
    if (!matrixData) return;
    setSaving(true);
    setMatrixData(null); // モーダルを即閉じる（CLAUDE.md: ボタン押下→モーダル閉じる）

    const entryAdd = Array.from(entrySet)
      .filter((k) => !initialEntrySet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });
    const entryRemove = Array.from(initialEntrySet)
      .filter((k) => !entrySet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });

    const tossUpAdd = Array.from(tossUpSet)
      .filter((k) => !initialTossUpSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });
    const tossUpRemove = Array.from(initialTossUpSet)
      .filter((k) => !tossUpSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, product_id: b };
      });

    const closingPartnerAdd = Array.from(closingPartnerSet)
      .filter((k) => !initialClosingPartnerSet.has(k))
      .map((k) => {
        const [tuId, closerId] = k.split("_").map(Number);
        const tu = matrixData.toss_up_settings.find((t) => t.id === tuId)!;
        return {
          sale_company_id: tu.sale_company_id,
          product_id: tu.product_id,
          closer_company_id: closerId,
        };
      });
    const closingPartnerRemove = Array.from(initialClosingPartnerSet)
      .filter((k) => !closingPartnerSet.has(k))
      .map((k) => {
        const [tuId, closerId] = k.split("_").map(Number);
        const tu = matrixData.toss_up_settings.find((t) => t.id === tuId)!;
        return {
          sale_company_id: tu.sale_company_id,
          product_id: tu.product_id,
          closer_company_id: closerId,
        };
      });

    const closingCategoryAdd = Array.from(closingCategorySet)
      .filter((k) => !initialClosingCategorySet.has(k))
      .map((k) => {
        const [sg, cat] = k.split("_").map(Number);
        return { sale_company_group_id: sg, category_id: cat };
      });
    const closingCategoryRemove = Array.from(initialClosingCategorySet)
      .filter((k) => !closingCategorySet.has(k))
      .map((k) => {
        const [sg, cat] = k.split("_").map(Number);
        return { sale_company_group_id: sg, category_id: cat };
      });

    const collaborateAdd = Array.from(collaborateSet)
      .filter((k) => !initialCollaborateSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, collaborate_company_id: b };
      });
    const collaborateRemove = Array.from(initialCollaborateSet)
      .filter((k) => !collaborateSet.has(k))
      .map((k) => {
        const [a, b] = k.split("_").map(Number);
        return { sale_company_id: a, collaborate_company_id: b };
      });

    setIsLoading(true);
    const success = await postSaleCompanyMatrixSetting({
      entry: { add: entryAdd, remove: entryRemove },
      toss_up: { add: tossUpAdd, remove: tossUpRemove },
      closing_partner: { add: closingPartnerAdd, remove: closingPartnerRemove },
      closing_category: { add: closingCategoryAdd, remove: closingCategoryRemove },
      collaborate: { add: collaborateAdd, remove: collaborateRemove },
    });

    setSaving(false);
    setIsLoading(false);
    if (success) {
      onSaveSuccess?.();
      setSaveResult('success');
    } else {
      setSaveResult('error');
    }
  }

  const renderEntryTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderCellStyle}>パートナー名</TableCell>
            {matrixData.products.map((p) => (
              <TableCell key={p.id} sx={colHeaderCellStyle}>
                {p.high_rank_company_name ? `${p.high_rank_company_name} / ${p.product_name}` : p.product_name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCompanies.map((sc, i) => (
            <TableRow key={sc.id} sx={{ backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff' }}>
              <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 0, zIndex: 1, backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff', borderRight: '2px solid #e0e0e0' }}>
                {sc.sale_company_name}
              </TableCell>
              {matrixData.products.map((p) => {
                const k = `${sc.id}_${p.id}`;
                return (
                  <TableCell key={p.id} sx={bodyCheckboxCellStyle}>
                    <Checkbox
                      size="small"
                      sx={{ padding: "2px" }}
                      checked={entrySet.has(k)}
                      onChange={() => toggleSet(setEntrySet, k)}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  const renderTossUpTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderCellStyle}>パートナー名</TableCell>
            {matrixData.products.map((p) => (
              <TableCell key={p.id} sx={colHeaderCellStyle}>
                {p.high_rank_company_name ? `${p.high_rank_company_name} / ${p.product_name}` : p.product_name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCompanies.map((sc, i) => (
            <TableRow key={sc.id} sx={{ backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff' }}>
              <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 0, zIndex: 1, backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff', borderRight: '2px solid #e0e0e0' }}>
                {sc.sale_company_name}
              </TableCell>
              {matrixData.products.map((p) => {
                const k = `${sc.id}_${p.id}`;
                return (
                  <TableCell key={p.id} sx={bodyCheckboxCellStyle}>
                    <Checkbox
                      size="small"
                      sx={{ padding: "2px" }}
                      checked={tossUpSet.has(k)}
                      onChange={() => toggleSet(setTossUpSet, k)}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  const renderClosingPartnerTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);

    // パートナー名→商材名の順でソート
    const sortedTossUpSettings = [...matrixData.toss_up_settings].sort((a, b) => {
      const compA = matrixData.sale_companies.find((sc) => sc.id === a.sale_company_id);
      const compB = matrixData.sale_companies.find((sc) => sc.id === b.sale_company_id);
      const prodA = matrixData.products.find((p) => p.id === a.product_id);
      const prodB = matrixData.products.find((p) => p.id === b.product_id);
      const nameComp = (compA?.sale_company_name ?? "").localeCompare(compB?.sale_company_name ?? "", "ja");
      if (nameComp !== 0) return nameComp;
      return (prodA?.product_name ?? "").localeCompare(prodB?.product_name ?? "", "ja");
    });

    const rows: React.ReactNode[] = [];
    let lastCompanyId: number | null = null;
    let dataRowIndex = 0;

    sortedTossUpSettings.forEach((tu) => {
      if (tu.sale_company_id !== lastCompanyId) {
        lastCompanyId = tu.sale_company_id;
        const company = matrixData.sale_companies.find((sc) => sc.id === tu.sale_company_id);
        rows.push(
          <TableRow key={`sep_${tu.sale_company_id}`} sx={{ bgcolor: "grey.100" }}>
            <TableCell
              colSpan={sortedCompanies.length + 1}
              sx={{ fontWeight: "bold", ...headerCellStyle }}
            >
              {company?.sale_company_name ?? ""}
            </TableCell>
          </TableRow>
        );
      }
      const rowBg = dataRowIndex % 2 === 0 ? '#f5f5f5' : '#ffffff';
      const tuProduct = matrixData.products.find((p) => p.id === tu.product_id);
      rows.push(
        <TableRow key={tu.id} sx={{ backgroundColor: rowBg }}>
          <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 0, zIndex: 1, backgroundColor: rowBg, borderRight: '2px solid #e0e0e0' }}>
            {tuProduct?.product_name ?? ""}
          </TableCell>
          {sortedCompanies.map((colSc) => {
            const isSelf = tu.sale_company_id === colSc.id;
            const k = `${tu.id}_${colSc.id}`;
            return (
              <TableCell
                key={colSc.id}
                sx={{ padding: "4px 2px", borderRight: '1px solid #e0e0e0', textAlign: 'center' }}
              >
                {isSelf ? (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", lineHeight: "34px", userSelect: "none" }}
                  >
                    -
                  </Typography>
                ) : (
                  <Checkbox
                    size="small"
                    sx={{ padding: "2px" }}
                    checked={closingPartnerSet.has(k)}
                    onChange={() => toggleSet(setClosingPartnerSet, k)}
                  />
                )}
              </TableCell>
            );
          })}
        </TableRow>
      );
      dataRowIndex++;
    });

    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderCellStyle}>商材名</TableCell>
            {sortedCompanies.map((sc) => (
              <TableCell key={sc.id} sx={colHeaderCellStyle}>
                {sc.sale_company_name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </>
    );
  };

  const renderClosingCategoryTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    const companyOrderMap = new Map(sortedCompanies.map((sc, idx) => [sc.id, idx]));
    const sortedGroups = [...matrixData.sale_company_groups].sort((a, b) => {
      const orderA = companyOrderMap.get(a.sale_company_id) ?? 0;
      const orderB = companyOrderMap.get(b.sale_company_id) ?? 0;
      return orderA - orderB;
    });

    const rows: React.ReactNode[] = [];
    let lastCompanyId: number | null = null;
    let dataRowIndex = 0;

    sortedGroups.forEach((sg) => {
      if (sg.sale_company_id !== lastCompanyId) {
        lastCompanyId = sg.sale_company_id;
        const company = matrixData.sale_companies.find(
          (sc) => sc.id === sg.sale_company_id
        );
        rows.push(
          <TableRow key={`sep_${sg.sale_company_id}`} sx={{ bgcolor: "grey.100" }}>
            <TableCell
              colSpan={matrixData.categories.length + 1}
              sx={{ fontWeight: "bold", ...headerCellStyle }}
            >
              {company?.sale_company_name ?? ""}
            </TableCell>
          </TableRow>
        );
      }
      const rowBg = dataRowIndex % 2 === 0 ? '#f5f5f5' : '#ffffff';
      rows.push(
        <TableRow key={sg.id} sx={{ backgroundColor: rowBg }}>
          <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 0, zIndex: 1, backgroundColor: rowBg, borderRight: '2px solid #e0e0e0' }}>
            {sg.sale_company_group_name}
          </TableCell>
          {matrixData.categories.map((cat) => {
            const k = `${sg.id}_${cat.id}`;
            return (
              <TableCell key={cat.id} sx={bodyCheckboxCellStyle}>
                <Checkbox
                  size="small"
                  sx={{ padding: "2px" }}
                  checked={closingCategorySet.has(k)}
                  onChange={() => toggleSet(setClosingCategorySet, k)}
                />
              </TableCell>
            );
          })}
        </TableRow>
      );
      dataRowIndex++;
    });

    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderCellStyle}>グループ名</TableCell>
            {matrixData.categories.map((cat) => (
              <TableCell key={cat.id} sx={colHeaderCellStyle}>
                {cat.category_name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </>
    );
  };

  const renderCollaborateTab = () => {
    if (!matrixData) return null;
    const sortedCompanies = [...matrixData.sale_companies].sort((a, b) => a.id - b.id);
    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeaderCellStyle}>パートナー名</TableCell>
            {sortedCompanies.map((col) => (
              <TableCell key={col.id} sx={colHeaderCellStyle}>
                {col.sale_company_name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCompanies.map((row, i) => (
            <TableRow key={row.id} sx={{ backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff' }}>
              <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 0, zIndex: 1, backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff', borderRight: '2px solid #e0e0e0' }}>
                {row.sale_company_name}
              </TableCell>
              {sortedCompanies.map((col) => {
                const isSelf = row.id === col.id;
                const isMirror = row.id > col.id;
                const isDisabled = isSelf || isMirror;
                const canonicalKey = `${Math.min(row.id, col.id)}_${Math.max(row.id, col.id)}`;
                const isChecked = collaborateSet.has(canonicalKey);
                return (
                  <TableCell
                    key={col.id}
                    sx={{
                      padding: "4px 2px",
                      borderRight: '1px solid #e0e0e0',
                      textAlign: 'center',
                    }}
                  >
                    {isDisabled ? (
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "center", lineHeight: "34px", userSelect: "none" }}
                      >
                        -
                      </Typography>
                    ) : (
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={() => toggleSet(setCollaborateSet, canonicalKey)}
                        sx={{ padding: "2px" }}
                      />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  return (
    <Modal open={open && !loading && (!!matrixData || saveResult !== '')} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {SETTING_TYPE_LABEL[settingType] ?? "一括設定"} 一括設定
        </Typography>
        {saveResult !== '' ? (
          <>
            <Typography sx={{ mb: 2 }}>
              {saveResult === 'success'
                ? '更新が完了しました。'
                : '更新に失敗しました。再度お試しください。'}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="outlined"
                sx={{ bgcolor: "white" }}
                onClick={() => {
                  setSaveResult('');
                  onClose();
                }}
              >
                閉じる
              </Button>
            </Box>
          </>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: "60vh", overflow: "auto" }}
            >
              <Table stickyHeader size="small">
                {settingType === "entry" && renderEntryTab()}
                {settingType === "tossup" && renderTossUpTab()}
                {settingType === "closing_partner" && renderClosingPartnerTab()}
                {settingType === "closing_category" && renderClosingCategoryTab()}
                {settingType === "collaborate" && renderCollaborateTab()}
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <Button variant="outlined" sx={{ bgcolor: "white" }} onClick={handleSave}>
                保存
              </Button>
              <Button variant="outlined" sx={{ bgcolor: "white" }} onClick={onClose}>
                キャンセル
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
